import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, FormLabel, Icon, Select, Avatar, SimpleGrid, useColorModeValue, Spinner, Center, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Button, ButtonGroup, useDisclosure, Input, Text, Alert, AlertIcon, AlertDescription, HStack, Link } from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import { MdEmail, MdSearchOff } from "react-icons/md";
import { FaSlack } from "react-icons/fa";
import FiatTransactionsTable from "views/admin/dataTables/components/FiatTransactionsTable";

import Usa from "assets/img/dashboards/usa.png";

import useEndpoints from "providers/Endpoints";
import constants from "providers/Constants";

export default function Fiat() {
  const { t } = useTranslation();

  const selectStyle = {
    width: '100%',
    padding: '10px',
    marginBottom: '24px',
    borderRadius: '15px',
    borderColor: '#E2E8F0',
    borderWidth: '1px',
    fontSize: 'sm',
    fontWeight: '500',
  };

  const [wallet, setWallet] = useState({});
  const [currency, setCurrency] = useState(null);
  const [postData, setPostData] = useState({});
  const [error, setError] = React.useState([]);
  const [fiatProviders, setFiatProviders] = useState([]);
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState(0);
  const [wallets, setWallets] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen: isBTModalOpen, onOpen: onBTModalOpen, onClose: onBTModalClose } = useDisclosure();
  const { isOpen: isCWModalOpen, onOpen: onCWModalOpen, onClose: onCWModalClose } = useDisclosure();
  const { isOpen: isCFModalOpen, onOpen: onCFModalOpen, onClose: onCFModalClose } = useDisclosure();
  const { isOpen: isSendFiatModalOpen, onOpen: onSendFiatModalOpen, onClose: onSendFiatModalClose } = useDisclosure();

  // Chakra Color Mode
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const mutedBorder = useColorModeValue("gray.200", "whiteAlpha.300");
  const mutedIcon = useColorModeValue("gray.400", "whiteAlpha.500");
  const dropdownBg = useColorModeValue("white", "gray.700");
  const dropdownTextColor = useColorModeValue("gray.800", "white");

  // helpers
  const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const clearPricingFields = (extra = {}) => ({
    exchange_rate: null,
    sub_total: null,
    fee: null,
    total: null,
    conversion_id: null,
    fee_id: null,
    ...extra,
  });

  const endpoints = useEndpoints();

  useEffect(() => {
    getFiatWallets();
  }, []);

  if (loading) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }}>
      {/* Sandbox Mode Alert */}
      {constants.getCurrentEnvironment() === 'SANDBOX' && (
        <Alert status="warning" mb="20px" borderRadius="15px" p="20px">
          <AlertIcon />
          <Box flex="1">
            <AlertDescription>
              <Text mb="10px">
                {t('You are currently in')} <strong>{t('SANDBOX MODE')}</strong>. {t('If you wish to fund your sandbox wallets or need more tokens, feel free to reach out via our support channels.')}
              </Text>
              <HStack spacing="15px">
                <Link
                  href="https://cryptobeginnerhq.slack.com/#blockmap"
                  isExternal
                  display="flex"
                  alignItems="center"
                  bg="purple.500"
                  color="white"
                  px="12px"
                  py="6px"
                  borderRadius="md"
                  fontSize="sm"
                  fontWeight="500"
                  _hover={{ bg: "purple.600" }}
                >
                  <Icon as={FaSlack} mr="6px" />
                  {t('Slack')}
                </Link>
                <Link
                  href="mailto:support@trovilo.co"
                  isExternal
                  display="flex"
                  alignItems="center"
                  bg="blue.500"
                  color="white"
                  px="12px"
                  py="6px"
                  borderRadius="md"
                  fontSize="sm"
                  fontWeight="500"
                  _hover={{ bg: "blue.600" }}
                >
                  <Icon as={MdEmail} mr="6px" />
                  {t('Email')}
                </Link>
              </HStack>
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Action Buttons */}
      <SimpleGrid
        columns={{ base: 1, md: 1, lg: 1, "2xl": 1 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          controls={true}
          onControlClick={(action) => {
            if (action === 'create_wallet') {
              setCurrency(null);
              setPostData({});
              setFiatProviders([]);
              setError([]);
              return onCWModalOpen();
            }
            if (action === 'add_fiat') {
              setCurrency(null);
              setPostData({});
              setFiatProviders([]);
              setError([]);
              return getPaymentAccounts(wallet?.country_currency_short);
            }
            if (action === 'convert_balance') {
              setCurrency(null);
              setPostData({});
              setFiatProviders([]);
              setError([]);
              return onCFModalOpen();
            }
            if (action === 'send_fiat') {
              setCurrency(null);
              setPostData({});
              setFiatProviders([]);
              setError([]);
              return onSendFiatModalOpen();
            }
          }}
          endContent={
            <Flex me='-16px' mt='10px'>
              <FormLabel htmlFor='balance'>
                <Avatar src={wallet?.country_avatar || Usa} />
              </FormLabel>
              <Select
                onChange={(e) => {
                  const wallet = wallets.find((item) => item?.country_currency_short === e.target.value);
                  setWallet(wallet);
                }}
                id='balance'
                variant='filled'
                mt='5px'
                value={wallet?.country_currency_short}
                me='20px'
                defaultValue='usd'>
                {
                  wallets.map((item, index) => {
                    return (
                      <option key={index} value={item?.country_currency_short}>
                        {item?.country_currency_short}
                      </option>
                    );
                  })
                }
              </Select>
            </Flex>
          }
          name='Your Fiat Balance'
          value={`${wallet?.country_currency_short || 'N/A'} ${Number(wallet?.available_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`}
        />
      </SimpleGrid>

      <SimpleGrid
        mb='20px'
        columns={{ sm: 1, md: 1 }}
        spacing={{ base: "20px", xl: "20px" }}>
        <FiatTransactionsTable
          sortArray={[1, 5, 10, 50, 100]}
          index={(value) => { }}
          tableData={transactions}
        />
      </SimpleGrid>

      {/* Bank transfer static account  */}
      <Modal isOpen={isBTModalOpen} onClose={onBTModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Virtual Account Details')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {paymentAccounts?.length > 0 && (
              <Flex w="100%" justify="center" mb="20px">
                <ButtonGroup isAttached variant="outline" size="sm">
                  {paymentAccounts.map((acc, i) => {
                    const code = acc?.currency || 'N/A';
                    const isActive = selectedPaymentAccount === i;
                    return (
                      <Button
                        key={i}
                        onClick={() => setSelectedPaymentAccount(i)}
                        variant={isActive ? 'solid' : 'outline'}
                        colorScheme="brand"
                      >
                        {code}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </Flex>
            )}

            <SimpleGrid mt={'10px'} columns={{ base: 1, md: 2 }} spacing={5}>
              <Box>
                <FormLabel>{t('Bank Name')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.bank_name || 'N/A'}</Box>
              </Box>
              <Box>
                <FormLabel>{t('Bank Code')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.bank_code || 'N/A'}</Box>
              </Box>
              <Box>
                <FormLabel>{t('Account Number')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.account_number || 'N/A'}</Box>
              </Box>
              <Box>
                <FormLabel>{t('Account Name')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.account_name || 'N/A'}</Box>
              </Box>
              <Box>
                <FormLabel>{t('Currency')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.currency || paymentAccounts?.[selectedPaymentAccount]?.currency_code || paymentAccounts?.[selectedPaymentAccount]?.country_currency_short || 'N/A'}</Box>
              </Box>
              <Box>
                <FormLabel>{t('Expiry Date')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">
                  {paymentAccounts?.[selectedPaymentAccount]?.expiry_date
                    ? (() => {
                      const date = new Date(paymentAccounts?.[selectedPaymentAccount]?.expiry_date);
                      return isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
                    })()
                    : "N/A"}
                </Box>
              </Box>
              <Box>
                <FormLabel>{t('Status')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.status || 'N/A'}</Box>
              </Box>
              <Box>
                <FormLabel>{t('Arrival Time')}</FormLabel>
                <Box p={2} bg={boxBg} borderRadius="md">{paymentAccounts?.[selectedPaymentAccount]?.arrival_time || 'N/A'}</Box>
              </Box>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              mr={3} onClick={onBTModalClose}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='outline'
              fontWeight='500'>
              {t('Cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create fiat wallet  */}
      <Modal isOpen={isCWModalOpen} onClose={onCWModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Create Fiat Wallet')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={selectStyle}
            >
              <option value="" disabled selected>{t('Select Currency')}</option>
              {
                currencies && currencies.map((item, i) => (
                  <option key={i} value={item.currency_code}>{item.currency_code} - {item.currency_symbol}</option>
                ))
              }
            </select>
          </ModalBody>

          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              mr={3} onClick={onCWModalClose}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='ghost'
              fontWeight='500'>
              {t('Close')}
            </Button>
            <Button
              isLoading={loading}
              isDisabled={loading}
              me='100%'
              mb='10px'
              w='140px'
              mr={1} onClick={() => {
                createFiatWallet(currency);
              }}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='brand'
              fontWeight='500'>
              {t('Proceed')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Convert fiat */}
      <Modal isOpen={isCFModalOpen} onClose={onCFModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Convert Fiat')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              value={postData?.from_currency}
              onChange={(e) => setPostData((prev) => ({ ...prev, from_currency: e.target.value, ...clearPricingFields() }))}
              style={selectStyle}
            >
              <option value="" disabled selected>{t('Select From Currency')}</option>
              {
                wallets && wallets.map((item, i) => (
                  <option key={i} value={item.country_currency_short}>
                    {item.country_currency_short} - {item.country_currency_symbol}
                    {Number(item.available_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </option>
                ))
              }
            </select>
            <select
              required
              value={postData?.to_currency}
              onChange={(e) => setPostData((prev) => ({ ...prev, to_currency: e.target.value, ...clearPricingFields() }))}
              style={selectStyle}
            >
              <option value="" disabled selected>{t('Select To Currency')}</option>
              {
                wallets && wallets.map((item, i) => (
                  <option key={i} value={item.country_currency_short}>
                    {item.country_currency_short} - {item.country_currency_symbol}
                    {Number(item.available_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </option>
                ))
              }
            </select>
            {
              postData?.from_currency && postData?.to_currency &&
              <Input
                isRequired={true}
                variant='auth'
                fontSize='sm'
                value={postData?.amount}
                type={'number'}
                ms={{ base: "0px", md: "0px" }}
                onChange={e => {
                  setPostData((prev) => ({ ...prev, amount: e.target.value, ...clearPricingFields() }))
                }}
                placeholder={`${t('Amount in')} ${postData?.from_currency}`}
                mb='10px'
                fontWeight='500'
                size='lg'
              />
            }
            {postData?.from_currency && postData?.total && postData?.to_currency && postData?.conversion_id && postData?.fee_id && (
              <Text fontSize='xs' color={'green'} ml='5px'>
                {t('Your conversion amount is')} {money(postData?.sub_total)} {postData?.sub_currency || t('N/A')}. {t('You will be charged a fee of')} {money(postData?.fee)} {postData?.fee_currency || t('N/A')}
              </Text>
            )}

            {
              error.length > 0 && (
                error.map((err, index) => (
                  <Text
                    key={index}
                    mt='10px'
                    ms='4px'
                    color={'red'}
                    fontWeight='400'
                    fontSize='sm'>
                    * {err.msg}
                  </Text>
                ))
              )
            }
          </ModalBody>

          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              mr={3} onClick={onCFModalClose}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='ghost'
              fontWeight='500'>
              {t('Close')}
            </Button>
            {
              !postData?.from_currency || !postData?.to_currency || !postData?.conversion_id || !postData?.fee_id || !postData?.total ? (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='140px'
                  mr={1} onClick={() => {
                    getConversionRate(postData?.from_currency, postData?.to_currency, postData?.amount, 'buying_price', postData?.provider, () => {
                      getFiatFee('convert_balance', postData?.amount, postData?.from_currency, postData?.to_currency, postData?.provider, postData?.country_shortcode, () => { })
                    })
                  }}
                  minW='140px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Verify')}
                </Button>
              ) : (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='250px'
                  mr={1} onClick={() => {
                    return convertFiat(postData);
                  }}
                  minW='140px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Convert')} {money(postData?.total)} {postData?.sub_currency || 'N/A'}
                </Button>
              )
            }
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send fiat modal  */}
      <Modal isOpen={isSendFiatModalOpen} onClose={onSendFiatModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Send Fiat')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select
              required
              onChange={(e) => {
                const selected = currencies.find((item) => item.currency_code === e.target.value);
                setPostData((prev) => ({ ...prev, currency: e.target.value, country_shortcode: selected?.country_shortcode, provider_code: null, provider_name: null, ...clearPricingFields() }));
                if (e.target.value && postData?.category) { getFiatProviders(selected?.country_shortcode, postData?.category); }
              }}
              value={postData?.currency}
              style={selectStyle}
            >
              <option value="" disabled selected>{t('Select Currency')}</option>
              {
                currencies && currencies.map((item, i) => (
                  <option key={i} value={item.currency_code}>{item.currency_code} - {item.currency_symbol}</option>
                ))
              }
            </select>
            <select
              required
              onChange={(e) => {
                setPostData((prev) => ({ ...prev, category: e.target.value, provider_code: null, provider_name: null, ...clearPricingFields() }))
                if (postData?.country_shortcode && e.target.value) { return getFiatProviders(postData?.country_shortcode, e.target.value); }
              }}
              value={postData?.category}
              style={selectStyle}
            >
              <option value="" disabled selected>{t('Select Category')}</option>
              <option key={0} value={'bank'}>{t('Bank')}</option>
            </select>
            {
              postData?.category && postData?.currency && <>
                {
                  fiatProviders?.length > 0 ? (
                    <>
                      <Box position="relative" mb='20px'>
                        <Input
                          placeholder={t('Select Provider')}
                          value={providerSearchQuery}
                          onChange={(e) => setProviderSearchQuery(e.target.value)}
                          onFocus={() => setIsProviderDropdownOpen(true)}
                          onClick={() => setIsProviderDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setIsProviderDropdownOpen(false), 150)}
                          fontSize='sm'
                          fontWeight='500'
                          variant='auth'
                          size='lg'
                        />
                        {isProviderDropdownOpen && (
                          <Box
                            position="absolute"
                            top="100%"
                            left="0"
                            right="0"
                            borderWidth="1px"
                            borderColor={mutedBorder}
                            borderRadius="md"
                            maxH="200px"
                            overflowY="auto"
                            bg={dropdownBg}
                            zIndex="10"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {
                              fiatProviders &&
                              [...fiatProviders]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .filter((item) => item.name.toLowerCase().includes(providerSearchQuery.toLowerCase()))
                                .map((item, i) => (
                                  <Box
                                    key={i}
                                    p='10px'
                                    borderBottomWidth={i < fiatProviders.length - 1 ? '1px' : '0px'}
                                    borderColor={mutedBorder}
                                    cursor='pointer'
                                    bg={postData?.provider_code === item.code ? 'brand.500' : 'transparent'}
                                    color={postData?.provider_code === item.code ? 'white' : dropdownTextColor}
                                    _hover={{ bg: postData?.provider_code === item.code ? 'brand.600' : boxBg }}
                                    onClick={() => {
                                      setPostData((prev) => ({ ...prev, provider_code: item.code, provider_name: item.name, can_resolve: item.can_resolve, ...clearPricingFields() }));
                                      setProviderSearchQuery('');
                                      setIsProviderDropdownOpen(false);
                                    }}
                                  >
                                    <Text fontSize='sm' fontWeight='500'>{item.name}</Text>
                                  </Box>
                                ))
                            }
                            {
                              fiatProviders.filter((item) => item.name.toLowerCase().includes(providerSearchQuery.toLowerCase())).length === 0 && providerSearchQuery && (
                                <Box p='10px' textAlign='center'>
                                  <Text fontSize='sm' color='gray.500'>{t('No results found!')}</Text>
                                </Box>
                              )
                            }
                          </Box>
                        )}
                      </Box>
                      {postData?.provider_code && (
                        <Text fontSize='sm' fontWeight='500' mb='20px' p='10px' bg={boxBg} borderRadius='md' size='lg'>
                          {t('Selected')}: {postData?.provider_name}
                        </Text>
                      )}
                      <Input
                        isRequired={true}
                        variant='auth'
                        fontSize='sm'
                        value={postData?.account_number}
                        type={'number'}
                        ms={{ base: "0px", md: "0px" }}
                        onChange={e => {
                          setPostData((prev) => ({
                            ...prev,
                            account_number: e.target.value,
                            account_name: null,
                            ...clearPricingFields()
                          }))
                        }}
                        placeholder={t('Account Number')}
                        mb='20px'
                        fontWeight='500'
                        size='lg'
                      />
                      {
                        !postData?.can_resolve || postData?.can_resolve && postData?.account_name ?
                          <Input
                            isRequired={true}
                            variant='auth'
                            fontSize='sm'
                            value={postData?.account_name}
                            type={'text'}
                            ms={{ base: "0px", md: "0px" }}
                            onChange={e => {
                              setPostData((prev) => ({
                                ...prev,
                                account_name: e.target.value
                              }))
                            }}
                            placeholder={t('Account Name')}
                            mb='20px'
                            fontWeight='500'
                            size='lg'
                          /> : null
                      }
                      <Input
                        isRequired={true}
                        variant='auth'
                        fontSize='sm'
                        value={postData?.amount}
                        type={'number'}
                        ms={{ base: "0px", md: "0px" }}
                        onChange={e => {
                          setPostData((prev) => ({
                            ...prev,
                            amount: e.target.value,
                            ...clearPricingFields()
                          }))
                        }}
                        placeholder={`${t('Amount in')} ${postData?.currency}`}
                        mb='20px'
                        fontWeight='500'
                        size='lg'
                      />
                      <select
                        required
                        onChange={(e) => {
                          setPostData((prev) => ({ ...prev, debit_currency: e.target.value, ...clearPricingFields() }))
                        }}
                        value={postData?.debit_currency}
                        style={selectStyle}
                      >
                        <option value="" disabled selected>{t('Select Debit Wallet')}</option>
                        {
                          wallets && wallets.map((item, i) => (
                            <option key={i} value={item.country_currency_short}>
                              {item.country_currency_short} - {item.country_currency_symbol}
                              {Number(item.available_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </option>
                          ))
                        }
                      </select>
                      <Input
                        isRequired={true}
                        variant='auth'
                        fontSize='sm'
                        value={postData?.narration}
                        type={'text'}
                        ms={{ base: "0px", md: "0px" }}
                        onChange={e => {
                          setPostData((prev) => ({
                            ...prev,
                            narration: e.target.value
                          }))
                        }}
                        placeholder={t('Narration')}
                        mb='10px'
                        fontWeight='500'
                        size='lg'
                      />
                    </>
                  ) : (
                    <Center
                      borderWidth="1px"
                      borderStyle="dashed"
                      borderColor={mutedBorder}
                      bg={boxBg}
                      borderRadius="lg"
                      p="24px"
                      textAlign="center"
                      flexDir="column"
                      mb="20px"
                    >
                      <Icon as={MdSearchOff} w={10} h={10} color={mutedIcon} mb={3} />
                      <Text fontSize="md" fontWeight="600" mb="2">
                        {t('No providers to show')}
                      </Text>
                      <Text fontSize='sm' color={'gray.500'}>
                        {t('Select a currency and category to see available providers.')}
                      </Text>
                    </Center>
                  )
                } </>
            }

            {postData?.currency && postData?.debit_currency && postData?.total && postData?.conversion_id && postData?.fee_id && (
              <Text fontSize='xs' color={'green'} ml='5px'>
                {t('You would be charged a total of')} {postData?.total ? money(postData.total) : 0} {postData?.sub_currency || t('N/A')}. {t('This includes a fee of')} {postData?.fee ? money(postData.fee) : 0} {postData?.fee_currency || t('N/A')}
              </Text>
            )}

            {
              error.length > 0 && (
                error.map((err, index) => (
                  <Text
                    key={index}
                    mt='10px'
                    ms='4px'
                    color={'red'}
                    fontWeight='400'
                    fontSize='sm'>
                    * {err.msg}
                  </Text>
                ))
              )
            }
          </ModalBody>

          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              mr={3} onClick={onSendFiatModalClose}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='ghost'
              fontWeight='500'>
              {t('Close')}
            </Button>
            {
              fiatProviders?.length > 0 && (
                !postData?.account_name || !postData?.provider_code || !postData?.conversion_id || !postData?.fee_id || !postData?.total ? (
                  <Button
                    isLoading={loading}
                    isDisabled={loading}
                    me='100%'
                    mb='10px'
                    w='140px'
                    mr={1} onClick={async () => {
                      resolveFiatBeneficiaryDetails(() => {
                        getConversionRate(postData?.currency, postData?.debit_currency, postData?.amount, 'selling_price', postData?.provider, () => {
                          getFiatFee('send_fiat', postData?.amount, postData?.currency, postData?.debit_currency, postData?.provider, postData?.country_shortcode, () => { })
                        })
                      })
                    }}
                    minW='140px'
                    mt={{ base: "20px", "2xl": "auto" }}
                    variant='brand'
                    fontWeight='500'>
                    {t('Verify')}
                  </Button>
                ) : (
                  <Button
                    isLoading={loading}
                    isDisabled={loading}
                    me='100%'
                    mb='10px'
                    w='140px'
                    mr={1} onClick={() => {
                      return sendFiat('send_fiat');
                    }}
                    minW='140px'
                    mt={{ base: "20px", "2xl": "auto" }}
                    variant='brand'
                    fontWeight='500'>
                    {t('Send')}
                  </Button>
                )
              )
            }
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );

  // Get fiat wallets
  function getFiatWallets() {
    setLoading(true);
    endpoints.getFiatWallets((error, data) => {
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setWallet(data[0] || {});
        setWallets(data || []);
      }
      getFiatTransactions();
    });
  }

  // Get fiat wallets transactions
  function getFiatTransactions(start_item = 0, end_item = 100, page = 1) {
    endpoints.getFiatTransactions(start_item, end_item, page, (error, data) => {
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setTransactions(data || []);
      }
      getFiatCurrencies();
    });
  }

  // Get fiat currencies
  function getFiatCurrencies() {
    endpoints.getFiatCurrencies((error, data) => {
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setCurrencies(data || []);
      }
      setLoading(false);
    });
  }

  // Get payment accounts
  function getPaymentAccounts(currency) {
    setLoading(true);
    endpoints.getPaymentAccounts(currency, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setPaymentAccounts(data || []);
        onBTModalOpen();
      }
    });
  }

  // Create fiat wallet
  function createFiatWallet(currency) {
    if (!currency) {
      return constants.showToast("error", t('FAILED'), t('Please select a currency'));
    }
    setLoading(true);
    endpoints.createFiatWallet(currency, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        getFiatWallets();
        onCWModalClose();
        constants.showToast("success", t('SUCCESS'), t('Fiat wallet created successfully!'));
      }
    });
  }

  // Get fiat providers
  function getFiatProviders(country_shortcode, category) {
    if (!country_shortcode || !category) {
      return constants.showToast("error", t('FAILED'), t('Please select a currency and category'));
    }
    setLoading(true);
    endpoints.getFiatProviders(country_shortcode, category, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setFiatProviders(data || []);
      }
    });
  }

  // Resolve fiat beneficiary details
  function resolveFiatBeneficiaryDetails(callback) {
    if (!postData?.account_number || !postData?.country_shortcode || !postData?.provider_code || !postData?.category
      || !postData?.currency || !postData?.debit_currency || !postData?.amount
    ) {
      return constants.showToast("error", t('FAILED'), t('Please fill all the fields'));
    }
    setLoading(true);
    endpoints.resolveFiatBeneficiaryDetails(postData, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setPostData((prev) => ({
          ...prev,
          account_name: data?.account_name || null,
          account_number: data?.account_number || null,
          provider: data?.provider || null,
        }));
      }
      callback?.();
    });
  }

  // Get conversion rate
  function getConversionRate(from_currency, to_currency, amount, category, provider = null, callback) {
    if (!from_currency || !to_currency || !amount) { return constants.showToast("error", t('FAILED'), t('Please fill all the fields')); }
    setLoading(true);
    endpoints.getConversionRates(amount, from_currency, to_currency, category, provider, null, null, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setPostData((prev) => ({
          ...prev,
          conversion_id: data?.conversion_id,
          exchange_rate: Number(data?.exchange_rate),
          sub_currency: data?.currency,
          sub_total: Number(data?.exchange_rate),
        }));
      }
      callback?.();
    });
  }

  // Get fiat fee
  function getFiatFee(identifier, amount, currency, debit_currency, provider, country_shortcode, callback) {
    setLoading(true);
    endpoints.getFees(identifier, amount, currency, debit_currency, provider, identifier, country_shortcode, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setPostData((prev) => ({
          ...prev,
          fee_id: data?.fee_id,
          fee_currency: data?.currency,
          fee: Number(data?.fee),
          total: Number(prev?.sub_total || 0) + Number(data?.fee || 0),
        }));
      }
      callback?.();
    });
  }

  // Send fiat
  function sendFiat(identifier) {
    if (!postData?.account_number || !postData?.account_name || !postData?.amount || !postData?.currency || !postData?.provider_code
      || !postData?.provider_code || !postData?.category || !postData?.country_shortcode || !postData?.debit_currency
    ) {
      return constants.showToast("error", t('FAILED'), t('Please fill all the fields'));
    }
    setLoading(true);
    endpoints.sendFiat(postData?.category, {
      provider_code: postData?.provider_code,
      provider_name: postData?.provider_name,
      account_number: postData?.account_number,
      account_name: postData?.account_name,
      currency: postData?.currency,
      amount: postData?.amount,
      provider: postData?.provider,
      identifier: identifier,
      debit_currency: postData?.debit_currency,
      country_shortcode: postData?.country_shortcode,
      narration: postData?.narration
    }, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        getFiatWallets();
        onSendFiatModalClose();
        constants.showToast("success", t('SUCCESS'), t('Fiat sent successfully!'));
      }
    });
  }

  // Convert fiat
  function convertFiat(data) {
    if (!data?.from_currency || !data?.to_currency || !data?.amount) {
      return constants.showToast("error", t('FAILED'), t('Please fill all the fields'));
    }
    setLoading(true);
    endpoints.convertFiat(data?.from_currency, data?.to_currency, data?.amount, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        getFiatWallets();
        onCFModalClose();
        constants.showToast("success", t('SUCCESS'), t('Fiat converted successfully!'));
      }
    });
  }

}
